import { forwardRef, useImperativeHandle } from 'react'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { Stripe } from '@stripe/stripe-js'
import { Box, Typography } from '@mui/material'
import { IStripeCardFormRef } from '../../core/types/Stripe'
import { useTranslation } from 'react-i18next'

interface StripeCardFormProps {
  stripePromise: Promise<Stripe | null> | null
  buyerName: string
  buyerEmail: string
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontFamily: 'Montserrat, sans-serif',
      fontSize: '16px',
      color: '#424770',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: { color: '#9e2146' },
  },
}

const StripeCardFormInner = forwardRef<IStripeCardFormRef, { buyerName: string; buyerEmail: string }>(
  ({ buyerName, buyerEmail }, ref) => {
    const stripe = useStripe()
    const elements = useElements()

    useImperativeHandle(ref, () => ({
      tokenize: async () => {
        if (!stripe || !elements) {
          return { error: 'Stripe not initialized' }
        }
        const cardElement = elements.getElement(CardElement)
        if (!cardElement) {
          return { error: 'Card element not found' }
        }
        const { paymentMethod, error } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: buyerName,
            email: buyerEmail,
          },
        })
        if (error) {
          return { error: error.message ?? 'Card tokenization failed' }
        }
        return { paymentMethodId: paymentMethod!.id }
      },
    }))

    return (
      <Box
        sx={{
          border: '1px solid #ccc',
          borderRadius: '4px',
          p: 1.5,
          mt: 1,
          '&:focus-within': { borderColor: '#1C52BD' },
        }}
      >
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </Box>
    )
  }
)

StripeCardFormInner.displayName = 'StripeCardFormInner'

const StripeCardForm = forwardRef<IStripeCardFormRef, StripeCardFormProps>(
  ({ stripePromise, buyerName, buyerEmail }, ref) => {
    const { t } = useTranslation()

    if (!stripePromise) {
      return (
        <Typography variant="body2" color="error">
          {t('stripe.configUnavailable')}
        </Typography>
      )
    }

    return (
      <Elements stripe={stripePromise}>
        <StripeCardFormInner ref={ref} buyerName={buyerName} buyerEmail={buyerEmail} />
      </Elements>
    )
  }
)

StripeCardForm.displayName = 'StripeCardForm'

export default StripeCardForm
